import clsx from 'clsx';
import React from 'react';
import Apple from '../../../../public/assets/icons/apple.svg';
import Google from '../../../../public/assets/icons/google.svg';
import Facebook from '../../../../public/assets/icons/facebook.svg';
import Email from '../../../../public/assets/icons/email.svg';

/**
 * Social sign-in, drawn as the DS field rather than a pill.
 *
 * It sits directly above the email inputs in both auth modals, so it takes
 * their shell verbatim — `rounded-none`, a 1px surface-300 border on
 * surface-000, 48px tall with 12px padding — and darkens the border to
 * surface-400 on hover exactly as the field does. The old version was a
 * `rounded-full` 22px pill on `border-grey5` with a `bg-main` hover, which
 * read as a different family of control stacked on the inputs.
 *
 * The icon is pinned to the left padding instead of sitting in a flex row so
 * the label stays optically centred in the field.
 */
export type ISocialLoginButtonProps = {
    network: 'google' | 'apple' | 'facebook' | 'email';
    children: string;
    disabled?: boolean
    onClick: () => void;
};

const SocialLoginButton = (props: ISocialLoginButtonProps) => {
    const { network, children, onClick, disabled } = props;

    const icon = () => {
        switch (network) {
            case 'apple':
                return <Apple className="w-[24px] h-[24px]" />;
            case 'email':
                return <Email className="w-[24px] h-[24px]" />;
            case 'google':
                return <Google className="w-[24px] h-[24px]" />;
            case 'facebook':
                return <Facebook className="w-[24px] h-[24px]" />;
            default:
                break;
        }
        return <Google />;
    };

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={clsx(
                'text-body-400 group relative flex h-[48px] w-full items-center justify-center',
                'rounded-none border border-surface-300 bg-surface-000 p-[12px] text-ink-400',
                'overflow-hidden transition-colors duration-press ease-ds-out',
                'enabled:cursor-pointer hover:enabled:border-surface-400 hover:enabled:bg-surface-100',
                'disabled:border-surface-300 disabled:bg-surface-200 disabled:text-surface-400',
                'disabled:!cursor-not-allowed [&_*]:disabled:!cursor-not-allowed'
            )}
        >
            <span className="absolute left-[12px] top-[calc(50%-12px)]">{icon()}</span>
            {children}
        </button>
    );
};

export default SocialLoginButton;
